import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { GenerateSlots } from "../../Service/Service";

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AvailabilityCalendar = ({
  weeklyAvailability,
  unavailableDates,
  startDate,
  endDate,
  state,
  setStateData,
  duration = 15,
  buffer = 5,
  breakEnabled = true,
  breakStart = "13:30",
  breakEnd = "14:30",
}: any) => {
  const [month, setMonth] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const rangeStart = dayjs(startDate);
  const rangeEnd = dayjs(endDate);

  const start = month.startOf("month");
  const end = month.endOf("month");

  const days: (dayjs.Dayjs | null)[] = [];
  for (let i = 0; i < start.day(); i++) days.push(null);
  for (let d = 1; d <= end.date(); d++) days.push(month.date(d));

  const isDisabled = (date: dayjs.Dayjs) =>
    date.isBefore(rangeStart, "day") || date.isAfter(rangeEnd, "day");

  const getRule = (date: dayjs.Dayjs) =>
    weeklyAvailability?.[date.format("dddd")];

  const isDayUnavailable = (date: dayjs.Dayjs) => {
    const key = date.format("YYYY-MM-DD");
    if (unavailableDates.includes(key)) return true;
    const rule = getRule(date);
    return !rule?.enabled;
  };

  const hasCustomOverride = (date: dayjs.Dayjs) => {
    const key = date.format("YYYY-MM-DD");
    return (state?.dateOverrides || []).some(
      (o: any) =>
        dayjs(o.date).format("YYYY-MM-DD") === key &&
        (o.inactiveSlots || []).length > 0
    );
  };

  const getInactiveSlotsForDate = (date: dayjs.Dayjs) => {
    const key = date.startOf("day").toISOString();
    const override = state?.dateOverrides?.find(
      (o: any) => dayjs(o.date).startOf("day").toISOString() === key
    );
    return override?.inactiveSlots || [];
  };

  useEffect(() => {
    if (!selectedDate) {
      setStateData?.({ slots: [] });
      return;
    }

    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const rule = getRule(selectedDate);
        if (!rule?.enabled) {
          setStateData?.({ slots: [] });
          return;
        }

        const response = await GenerateSlots({
          date: selectedDate.format("YYYY-MM-DD"),
          availableHours: [{ start: rule.start, end: rule.end }],
          meetingDuration: duration,
          buffer,
          breakEnabled,
          breakStart,
          breakEnd,
        });
        let slots = response?.slots || [];
        const inactiveSlots = getInactiveSlotsForDate(selectedDate);
        slots = slots.map((s: any) => ({
          ...s,
          active: !inactiveSlots.some(
            (i: any) => i.start === s.start && i.end === s.end
          ),
        }));
        setStateData?.({ slots });
      } catch (err) {
        console.error(err);
        setStateData?.({ slots: [] });
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    weeklyAvailability,
    duration,
    buffer,
    breakEnabled,
    breakStart,
    breakEnd,
  ]);

  const toggleSlot = (date: string, slot: any) => {
    const overrides = state.dateOverrides || [];
    const normalizeDate = (d: any) => dayjs(d).format("YYYY-MM-DD");
    const existing = overrides.find(
      (o: any) => normalizeDate(o.date) === date
    );

    let updatedOverrides;
    if (slot.active) {
      updatedOverrides = existing
        ? overrides.map((o: any) =>
            normalizeDate(o.date) === date
              ? {
                  ...o,
                  inactiveSlots: [
                    ...(o.inactiveSlots || []),
                    { start: slot.start, end: slot.end },
                  ],
                }
              : o
          )
        : [
            ...overrides,
            {
              date,
              inactiveSlots: [{ start: slot.start, end: slot.end }],
            },
          ];
    } else {
      updatedOverrides = overrides.map((o: any) =>
        normalizeDate(o.date) === date
          ? {
              ...o,
              inactiveSlots: (o.inactiveSlots || []).filter(
                (s: any) => !(s.start === slot.start && s.end === slot.end)
              ),
            }
          : o
      );
    }

    setStateData?.({
      slots: (state.slots || []).map((s: any) =>
        s.start === slot.start && s.end === slot.end
          ? { ...s, active: !slot.active }
          : s
      ),
      dateOverrides: updatedOverrides,
    });
  };

  return (
    <div className="flex min-h-[480px] flex-col lg:flex-row">
      {/* Calendar */}
      <div className="flex min-w-0 flex-1 flex-col border-b border-[#F1F5F9] p-4 lg:border-b-0 lg:border-r">
        <div className="mb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setMonth((m) => m.subtract(1, "month"))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"
            aria-label="Previous month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <p className="text-sm font-bold text-[#111827]">
            {month.format("MMMM YYYY")}
          </p>
          <button
            type="button"
            onClick={() => setMonth((m) => m.add(1, "month"))}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"
            aria-label="Next month"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </div>

        <div className="mb-2 grid grid-cols-7">
          {WEEK.map((day) => (
            <span
              key={day}
              className="text-center text-[10px] font-semibold uppercase tracking-wide text-[#9CA3AF]"
            >
              {day}
            </span>
          ))}
        </div>

        <div className="grid flex-1 grid-cols-7 gap-1.5">
          {days.map((date, index) => {
            if (!date) return <div key={`e-${index}`} />;

            const key = date.format("YYYY-MM-DD");
            const selected = selectedDate?.isSame(date, "day");
            const disabled = isDisabled(date);
            const unavailable = isDayUnavailable(date);
            const custom = hasCustomOverride(date);
            const rule = getRule(date);
            const title = disabled
              ? "Out of date range"
              : unavailable
              ? "Off on this day"
              : custom
              ? "Custom override"
              : `Available: ${rule?.start} - ${rule?.end}`;

            return (
              <button
                key={key}
                type="button"
                title={title}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) setSelectedDate(date);
                }}
                className={`flex min-h-[56px] flex-col items-center justify-center rounded-xl px-0.5 transition ${
                  disabled
                    ? "cursor-not-allowed bg-[#F9FAFB] opacity-40"
                    : selected
                    ? "border-2 border-[#A02448] bg-[#F9F0F4] shadow-sm"
                    : custom
                    ? "border border-[#C4B5FD] bg-[#F5F3FF] hover:-translate-y-px"
                    : unavailable
                    ? "border border-[#FCA5A5] bg-[#FEF2F2] hover:-translate-y-px"
                    : "border border-[#E5E7EB] bg-white hover:-translate-y-px hover:shadow-sm"
                }`}
              >
                <span
                  className={`text-[13px] ${
                    disabled
                      ? "font-semibold text-[#D1D5DB]"
                      : unavailable
                      ? "font-semibold text-[#EF4444]"
                      : selected || custom
                      ? "font-bold text-[#A02448]"
                      : "font-semibold text-[#111827]"
                  }`}
                >
                  {date.date()}
                </span>
                {!disabled ? (
                  unavailable ? (
                    <span className="mt-0.5 rounded bg-[#FEE2E2] px-1.5 text-[8px] font-bold uppercase tracking-wide text-[#EF4444]">
                      Off
                    </span>
                  ) : (
                    <span
                      className={`mt-0.5 max-w-full truncate px-0.5 text-[8px] font-medium ${
                        selected || custom ? "text-[#A02448]" : "text-[#6B7280]"
                      }`}
                    >
                      {rule?.start}–{rule?.end}
                    </span>
                  )
                ) : null}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-[#F3F4F6] pt-3">
          <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
            Available
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
            Not Available / Day Off
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
            <span className="h-2.5 w-2.5 rounded-full bg-[#A02448]" />
            Custom Override
          </span>
        </div>
      </div>

      {/* Slot overrides panel */}
      <div className="flex w-full shrink-0 flex-col bg-[#FAFBFC] p-4 lg:w-[280px]">
        <p className="mb-3 text-sm font-semibold text-[#111827]">
          {selectedDate
            ? `Slots for ${selectedDate.format("MMM DD, YYYY")}`
            : "Date Slot Overrides"}
        </p>

        {!selectedDate ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-white px-4 py-8 text-center">
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3F4F6] text-[#9CA3AF]">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </span>
            <p className="text-sm font-semibold text-[#374151]">
              No dates selected yet
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-[#9CA3AF]">
              Click on calendar dates to inspect and toggle available time slots.
            </p>
          </div>
        ) : loadingSlots ? (
          <div className="flex flex-1 flex-col items-center justify-center py-10">
            <span className="mb-2 inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#A02448] border-r-transparent" />
            <p className="text-xs text-[#6B7280]">Generating slots...</p>
          </div>
        ) : !state?.slots || state.slots.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white py-10">
            <p className="text-xs text-[#9CA3AF]">
              No available slots for this date
            </p>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <p className="mb-2 text-[11px] text-[#6B7280]">
              Click a slot to disable or enable it.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {state.slots.map((slot: any, i: number) => {
                const isActive = slot.active;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      toggleSlot(selectedDate.format("YYYY-MM-DD"), slot)
                    }
                    className={`inline-flex h-8 items-center gap-1 rounded-lg border px-2 text-[11px] font-medium transition ${
                      isActive
                        ? "border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46] hover:bg-[#D1FAE5]"
                        : "border-[#E5E7EB] bg-[#F3F4F6] text-[#6B7280] line-through hover:bg-[#E5E7EB]"
                    }`}
                  >
                    {slot.start} – {slot.end}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
